import React, { useCallback } from 'react';

import { flushSync } from 'react-dom';
import { useSnackbar } from 'notistack';
import { useHotkeys } from 'react-hotkeys-hook';
import { Video } from './Video.tsx';
import { ReceiverAnnotationOverlay } from './annotations/ReceiverAnnotationOverlay.tsx';
import { ConnectedRoom } from './useRoom.ts';
import { useSettings, VideoDisplayMode } from './settings.ts';
import { SettingDialog } from './SettingDialog.tsx';
import { LiquidGlassCard } from './components/glass/LiquidGlassCard.tsx';
import { LiquidButton } from './components/glass/LiquidButton.tsx';
import { MembersPanel } from './components/panels/MembersPanel.tsx';
import { Tooltip } from './components/ui/tooltip.tsx';
import { BackgroundWrapper } from './components/shared/BackgroundWrapper.tsx';
import { FooterBar } from './components/shared/FooterBar.tsx';
import { StreamAudioControl } from './components/shared/StreamAudioControl.tsx';
import { FocusedSidebarLayout } from './receiver-layout/index.ts';
import { WebRTCStatsFloating } from './webrtcStats.tsx';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Monitor,
    MonitorOff,
    Pause,
    Play,
    Maximize2,
    Minimize2,
    Volume2,
    VolumeX,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';

const HostStream: unique symbol = Symbol('mystream');

type GridVideoChildProps = React.VideoHTMLAttributes<HTMLVideoElement> & {
    onVideoRef?: (el: HTMLVideoElement | null) => void;
    /** Local grid state (mute/volume) still needs the underlying element. */
    onBridge?: (el: HTMLVideoElement | null) => void;
};

const GridVideoChild: React.FC<GridVideoChildProps> = ({onVideoRef, onBridge, ...rest}) => (
    <video
        {...rest}
        ref={(el) => {
            onBridge?.(el);
            onVideoRef?.(el);
        }}
    />
);

interface FullScreenHTMLVideoElement extends HTMLVideoElement {
    msRequestFullscreen?: () => void;
    mozRequestFullScreen?: () => void;
    webkitRequestFullscreen?: () => void;
}

const requestFullscreen = (element: FullScreenHTMLVideoElement | null) => {
    if (element?.requestFullscreen) {
        element.requestFullscreen();
    } else if (element?.mozRequestFullScreen) {
        element.mozRequestFullScreen();
    } else if (element?.msRequestFullscreen) {
        element.msRequestFullscreen();
    } else if (element?.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
    }
};

type FullscreenableHTMLElement = HTMLElement & {
    mozRequestFullScreen?: () => Promise<void> | void;
    msRequestFullscreen?: () => Promise<void> | void;
    webkitRequestFullscreen?: () => Promise<void> | void;
};

/** Browser fullscreen for the immersive receiver shell (hides browser chrome where supported). */
function requestContainerFullscreen(element: HTMLElement | null): Promise<void> {
    if (!element) return Promise.reject(new Error('No fullscreen element'));
    const el = element as FullscreenableHTMLElement;
    if (el.requestFullscreen) return el.requestFullscreen();
    if (el.webkitRequestFullscreen) {
        const r = el.webkitRequestFullscreen();
        return r != null && typeof (r as Promise<void>).then === 'function'
            ? (r as Promise<void>)
            : Promise.resolve();
    }
    if (el.mozRequestFullScreen) {
        const r = el.mozRequestFullScreen();
        return r != null && typeof (r as Promise<void>).then === 'function'
            ? (r as Promise<void>)
            : Promise.resolve();
    }
    if (el.msRequestFullscreen) {
        const r = el.msRequestFullscreen();
        return r != null && typeof (r as Promise<void>).then === 'function'
            ? (r as Promise<void>)
            : Promise.resolve();
    }
    return Promise.reject(new Error('Fullscreen API not supported'));
}

export const Room = ({
    state,
    share,
    stopShare,
    togglePauseShare,
    sharePauseLocal,
    setName,
    setRoomDisplayName,
    getPeerConnections,
    role,
    reportStreamFullscreen,
}: {
    state: ConnectedRoom;
    share: () => void;
    stopShare: () => void;
    togglePauseShare: () => void;
    sharePauseLocal: {isPaused: boolean; pauseMode: 'soft' | 'hard' | null};
    setName: (name: string) => void;
    setRoomDisplayName?: (name: string) => void;
    getPeerConnections: () => Record<string, RTCPeerConnection>;
    role?: string;
    reportStreamFullscreen: (userId: string | null) => void;
}) => {
    const [open, setOpen] = React.useState(false);
    const [settings, setSettings] = useSettings();
    const [showControl, setShowControl] = React.useState(true);
    const [hoverControl, setHoverControl] = React.useState(false);
    const [selectedStream, setSelectedStream] = React.useState<string | typeof HostStream>();
    const [videoElement, setVideoElement] = React.useState<FullScreenHTMLVideoElement | null>(
        null
    );
    const statsVideoRefs = React.useRef<Map<string, HTMLVideoElement>>(new Map());
    const [showMembers, setShowMembers] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(0);
    // gridView is constant based on role - setGridView intentionally unused
    const [gridView] = React.useState(role === 'receiver');
    const [viewMode, setViewMode] = React.useState<'democratic' | 'sidebar' | 'custom'>('democratic');
    const [fullscreenStreamId, setFullscreenStreamId] = React.useState<string | null>(null);
    const [forcedLayout, setForcedLayout] = React.useState<number | null>(null);
    const [mainStreamId, setMainStreamId] = React.useState<string | null>(null);
    const [previousViewMode, setPreviousViewMode] = React.useState<'democratic' | 'sidebar' | 'custom' | null>(null);
    const [hiddenUserIds, setHiddenUserIds] = React.useState<Set<string>>(new Set());
    /** Receiver grid: the one `<video>` that plays remote share audio (others stay muted). */
    const [receiverAudioTarget, setReceiverAudioTarget] = React.useState<{
        video: FullScreenHTMLVideoElement;
        stream: MediaStream;
    } | null>(null);
    const [, setReceiverAudioTracksEpoch] = React.useState(0);
    /** Applied to every receiver grid `<video>` (master control in footer). */
    const [receiverMasterVolume, setReceiverMasterVolume] = React.useState(1);
    const [receiverMasterMuted, setReceiverMasterMuted] = React.useState(false);
    /** Browser blocked unmuted playback until user gesture (autoplay policy). */
    const [receiverAudioPolicyBlocked, setReceiverAudioPolicyBlocked] = React.useState(false);
    /** Until true, keep the video element muted so picture keeps playing (muted silences audio only; unmute without gesture pauses). */
    const [receiverPlaybackUnlocked, setReceiverPlaybackUnlocked] = React.useState(false);
    const [receiverAnnotationEnabled, setReceiverAnnotationEnabled] = React.useState(false);
    const [receiverActiveAnnotationStreamId, setReceiverActiveAnnotationStreamId] = React.useState<string | null>(null);
    const receiverAudioVideoRef = React.useRef<HTMLVideoElement | null>(null);
    const audioPolicyHintShownRef = React.useRef(false);
    const receiverInitialAudioHintShownRef = React.useRef(false);
    /** Root for receiver immersive mode + `requestFullscreen` (browser chrome hidden where supported). */
    const receiverImmersiveRootRef = React.useRef<HTMLDivElement | null>(null);
    const previousViewModeRef = React.useRef(previousViewMode);
    const fullscreenStreamIdRef = React.useRef(fullscreenStreamId);
    /** Last `activeFullscreenPeerId` applied from the room — avoids re-sync when effect deps churn. */
    const prevActiveFullscreenPeerIdRef = React.useRef<string | null | undefined>(undefined);
    const roleRef = React.useRef(role);
    const { enqueueSnackbar } = useSnackbar();

    React.useEffect(() => {
        previousViewModeRef.current = previousViewMode;
    }, [previousViewMode]);
    React.useEffect(() => {
        fullscreenStreamIdRef.current = fullscreenStreamId;
    }, [fullscreenStreamId]);
    React.useEffect(() => {
        roleRef.current = role;
    }, [role]);

    const STREAMS_PER_PAGE = 16;

    // Dynamic grid layout based on number of streams
    const getGridLayout = (streamCount: number) => {
        if (streamCount === 1) return 'grid-cols-1 grid-rows-1';
        if (streamCount === 2) return 'grid-cols-2 grid-rows-1'; // Horizontal split (side-by-side)
        if (streamCount <= 4) return 'grid-cols-2 grid-rows-2';
        if (streamCount <= 6) return 'grid-cols-3 grid-rows-2';
        if (streamCount <= 9) return 'grid-cols-3 grid-rows-3';
        return 'grid-cols-4 grid-rows-4'; // 10-16 screens
    };

    const visibleStreams = state.clientStreams.slice(
        currentPage * STREAMS_PER_PAGE,
        (currentPage + 1) * STREAMS_PER_PAGE
    );
    const meUserId = state.users.find((u) => u.you)?.id ?? '';

    React.useEffect(() => {
        if (role !== 'receiver' || !import.meta.env.DEV) return;
        if (!meUserId) {
            console.warn(
                '[receiver-annotation] meUserId is empty — annotation toolbar will not show (overlay needs userId). Check that your user has `you: true` in state.users.'
            );
        }
    }, [role, meUserId]);

    // Get visible streams based on forced layout and hidden users
    const getVisibleStreams = () => {
        // Filter out hidden users
        const filteredStreams = state.clientStreams.filter(
            stream => !hiddenUserIds.has(stream.peer_id)
        );
        
        if (forcedLayout !== null) {
            // Force exact count: show first N streams
            return filteredStreams.slice(0, forcedLayout);
        }
        // Default: show paginated streams, filtered
        const startIndex = currentPage * STREAMS_PER_PAGE;
        const endIndex = startIndex + STREAMS_PER_PAGE;
        return filteredStreams.slice(startIndex, endIndex);
    };

    /** Streams that can host the global annotation UI (must match an on-screen tile). */
    const receiverAnnotationEligibleStreams = React.useMemo(() => {
        const filtered = state.clientStreams.filter((s) => !hiddenUserIds.has(s.peer_id));
        if (role !== 'receiver') return filtered;
        /** Immersive fullscreen shows only this stream — paginated grid slice would miss it. */
        if (fullscreenStreamId != null) {
            const fs = filtered.find((s) => s.id === fullscreenStreamId);
            return fs ? [fs] : [];
        }
        if (viewMode === 'sidebar') return filtered;
        if (forcedLayout !== null) return filtered.slice(0, forcedLayout);
        const start = currentPage * STREAMS_PER_PAGE;
        return filtered.slice(start, start + STREAMS_PER_PAGE);
    }, [role, viewMode, hiddenUserIds, forcedLayout, currentPage, state.clientStreams, fullscreenStreamId]);

    React.useEffect(() => {
        if (role !== 'receiver' || !import.meta.env.DEV) return;
        console.log('eligibleStreams', {
            ids: receiverAnnotationEligibleStreams.map((s) => s.id),
            count: receiverAnnotationEligibleStreams.length,
            viewMode,
            fullscreenStreamId,
            currentPage,
            forcedLayout,
            hiddenCount: hiddenUserIds.size,
        });
    }, [role, receiverAnnotationEligibleStreams, viewMode, fullscreenStreamId, currentPage, forcedLayout, hiddenUserIds.size]);

    // Get grid layout class based on forced layout or stream count
    const getGridLayoutClass = () => {
        const count = forcedLayout !== null ? forcedLayout : visibleStreams.length;
        return getGridLayout(count);
    };

    useShowOnMouseMovement(setShowControl);

    const handleFullscreen = useCallback(() => requestFullscreen(videoElement), [videoElement]);

    /** Immersive tile only (no browser fullscreen) — safe inside effects / remote sync. */
    const applyImmersiveFocus = useCallback(
        (streamId: string) => {
            setPreviousViewMode(viewMode);
            setFullscreenStreamId(streamId);
        },
        [viewMode]
    );

    // Fullscreen: Enter immersive tile + browser fullscreen (flushSync keeps user activation for API).
    const handleEnterFullscreen = useCallback(
        (streamId: string) => {
            flushSync(() => {
                applyImmersiveFocus(streamId);
            });
            const root = receiverImmersiveRootRef.current;
            if (root && document.fullscreenElement !== root) {
                void requestContainerFullscreen(root).catch((err) =>
                    console.warn('Browser fullscreen unavailable; using in-page immersive only.', err)
                );
            }
        },
        [applyImmersiveFocus]
    );

    // Fullscreen: Exit browser fullscreen first, then restore grid/sidebar.
    const handleExitFullscreen = useCallback(async () => {
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            }
        } catch {
            /* ignore */
        }
        if (previousViewMode) {
            setViewMode(previousViewMode);
        }
        setFullscreenStreamId(null);
        setPreviousViewMode(null);
    }, [previousViewMode]);

    /** Sync React when user leaves browser fullscreen (e.g. Esc) while in immersive receiver mode. */
    React.useEffect(() => {
        const onFullscreenChange = () => {
            if (roleRef.current !== 'receiver') return;
            if (fullscreenStreamIdRef.current === null) return;
            if (document.fullscreenElement != null) return;
            const pv = previousViewModeRef.current;
            if (pv) setViewMode(pv);
            setFullscreenStreamId(null);
            setPreviousViewMode(null);
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, []);

    // Sidebar (focused) view: swap main stage stream
    const handleSidebarFocusChange = (clickedStreamId: string) => {
        setMainStreamId(clickedStreamId);
    };

    /** View mode from footer: clear Members "custom" layout when leaving custom so sidebar / grid apply. */
    const handleViewModeChange = useCallback(
        (mode: 'democratic' | 'sidebar' | 'custom') => {
            if (mode === 'democratic' || mode === 'sidebar') {
                setForcedLayout(null);
            }
            setViewMode(mode);
        },
        []
    );

    // Layout: Handle layout selection from Members panel
    const handleLayoutChange = (layout: number | null) => {
        setForcedLayout(layout);
        if (layout !== null) {
            // Switch to Custom mode when layout is selected
            setViewMode('custom');
        } else {
            // Reset to Democratic when layout is cleared
            setViewMode('democratic');
        }
    };

    // Members Panel: Toggle fullscreen tile for this sender (enter / exit same stream)
    const handleMemberFullscreen = useCallback(
        (userId: string) => {
            const stream = state.clientStreams.find((s) => s.peer_id === userId);
            if (!stream) return;
            if (fullscreenStreamId === stream.id) {
                void handleExitFullscreen();
            } else {
                handleEnterFullscreen(stream.id);
            }
        },
        [state.clientStreams, fullscreenStreamId, handleEnterFullscreen, handleExitFullscreen]
    );

    /** Apply room-wide fullscreen target (from senders or other receivers) to local immersive view. */
    React.useEffect(() => {
        if (role !== 'receiver') return;
        const peer = state.activeFullscreenPeerId;
        if (peer === undefined) return;
        const prevPeer = prevActiveFullscreenPeerIdRef.current;
        if (peer === prevPeer) return;
        prevActiveFullscreenPeerIdRef.current = peer;

        if (peer === null || peer === '') {
            if (fullscreenStreamId != null) {
                void handleExitFullscreen();
            }
            return;
        }
        const stream = state.clientStreams.find((s) => s.peer_id === peer);
        if (!stream) return;
        if (fullscreenStreamId === stream.id) return;
        applyImmersiveFocus(stream.id);
    }, [
        role,
        state.activeFullscreenPeerId,
        state.clientStreams,
        fullscreenStreamId,
        applyImmersiveFocus,
        handleExitFullscreen,
    ]);

    const hasReportedStreamFullscreenRef = React.useRef(false);

    /** Receiver: broadcast local immersive focus so senders' member panels stay in sync. */
    React.useEffect(() => {
        if (role !== 'receiver') return;
        const peer =
            fullscreenStreamId != null
                ? state.clientStreams.find((c) => c.id === fullscreenStreamId)?.peer_id ?? null
                : null;
        if (peer == null && !hasReportedStreamFullscreenRef.current) {
            return;
        }
        hasReportedStreamFullscreenRef.current = true;
        reportStreamFullscreen(peer);
    }, [role, fullscreenStreamId, state.clientStreams, reportStreamFullscreen]);

    // Members Panel: Toggle hide/show user; exit fullscreen first if hiding the fullscreen tile
    const handleToggleHideUser = (userId: string) => {
        const stream = state.clientStreams.find((s) => s.peer_id === userId);
        if (stream && fullscreenStreamId === stream.id) {
            void handleExitFullscreen();
        }
        setHiddenUserIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    const localFullscreenPeerId =
        role === 'receiver' && fullscreenStreamId
            ? state.clientStreams.find((c) => c.id === fullscreenStreamId)?.peer_id ?? null
            : null;
    const membersPanelFullscreenPeerId =
        state.activeFullscreenPeerId !== undefined ? state.activeFullscreenPeerId : localFullscreenPeerId;

    /** Receiver: expanded single-stream view — hide footer; browser fullscreen + bottom audio only. */
    const receiverStreamFullscreen = role === 'receiver' && fullscreenStreamId != null;

    // Sidebar: Initialize focused stream when entering sidebar mode
    React.useEffect(() => {
        if (viewMode === 'sidebar' && !mainStreamId && state.clientStreams.length > 0) {
            setMainStreamId(state.clientStreams[state.clientStreams.length - 1].id);
        }
    }, [viewMode, mainStreamId, state.clientStreams]);

    // Sidebar: Reset focused stream if it disconnects
    React.useEffect(() => {
        if (mainStreamId && !state.clientStreams.find(c => c.id === mainStreamId)) {
            setMainStreamId(state.clientStreams[state.clientStreams.length - 1]?.id || null);
        }
    }, [state.clientStreams, mainStreamId]);

    // Exit immersive mode when that share ends (e.g. sender stopped; server sent endshare)
    React.useEffect(() => {
        if (role !== 'receiver' || !fullscreenStreamId) return;
        if (!state.clientStreams.some((c) => c.id === fullscreenStreamId)) {
            void handleExitFullscreen();
        }
    }, [role, state.clientStreams, fullscreenStreamId, handleExitFullscreen]);

    React.useEffect(() => {
        if (selectedStream === HostStream && state.hostStream) {
            return;
        }
        if (state.clientStreams.some(({ id }) => id === selectedStream)) {
            return;
        }
        if (state.clientStreams.length === 0 && selectedStream) {
            setSelectedStream(undefined);
            return;
        }
        setSelectedStream(state.clientStreams[0]?.id);
    }, [state.clientStreams, selectedStream, state.hostStream]);

    React.useEffect(() => {
        if (!receiverActiveAnnotationStreamId) return;
        if (!state.clientStreams.some((c) => c.id === receiverActiveAnnotationStreamId)) {
            setReceiverActiveAnnotationStreamId(null);
        }
    }, [receiverActiveAnnotationStreamId, state.clientStreams]);

    /** Keep annotation target on an on-screen stream when hiding users / pagination changes. */
    React.useEffect(() => {
        if (role !== 'receiver' || !receiverAnnotationEnabled) return;
        setReceiverActiveAnnotationStreamId((prev) => {
            if (receiverAnnotationEligibleStreams.length === 0) {
                console.log('syncActive → null (no eligible streams)', {prev});
                return null;
            }
            if (prev && receiverAnnotationEligibleStreams.some((s) => s.id === prev)) {
                return prev;
            }
            const next = receiverAnnotationEligibleStreams[0].id;
            console.log('syncActive → reassign', {prev, next, eligibleIds: receiverAnnotationEligibleStreams.map((s) => s.id)});
            return next;
        });
    }, [role, receiverAnnotationEnabled, receiverAnnotationEligibleStreams]);

    const stream =
        selectedStream === HostStream
            ? state.hostStream
            : state.clientStreams.find(({ id }) => selectedStream === id)?.stream;

    React.useEffect(() => {
        if (videoElement && stream) {
            // Check if stream has active video tracks
            const videoTracks = stream.getVideoTracks();
            if (videoTracks.length === 0) {
                console.warn('Stream has no video tracks');
                return;
            }

            // Log track state for debugging
            videoTracks.forEach((track, index) => {
                console.log(`Video track ${index}:`, {
                    enabled: track.enabled,
                    muted: track.muted,
                    readyState: track.readyState,
                    label: track.label
                });
            });

            videoElement.srcObject = stream;
            
            // IMPORTANT: Unmute video element for receivers
            // The sender's stream should have audio, so we want to hear it
            videoElement.muted = false;
            videoElement.volume = 1.0;
            
            // Wait for tracks to unmute before playing
            // Tracks often arrive muted and unmute within 100-500ms
            const playVideo = () => {
                videoElement.play().catch((err) => {
                    console.log('Could not play main video', err);
                    if (err.name === 'NotAllowedError') {
                        console.warn('Autoplay blocked - trying with muted first, then unmuting');
                        // Temporary mute to bypass autoplay policy
                        videoElement.muted = true;
                        videoElement
                            .play()
                            .then(() => {
                                // Once playing, unmute after a short delay
                                setTimeout(() => {
                                    videoElement.muted = false;
                                    console.log('Video unmuted after autoplay bypass');
                                }, 100);
                            })
                            .catch((retryErr) =>
                                console.log('Could not play main video with mute', retryErr)
                            );
                    }
                });
            };
            
            // Check if video track is muted
            const videoTrack = videoTracks[0];
            if (videoTrack.muted) {
                console.log('Video track is muted, waiting for unmute...');
                // Wait for track to unmute
                const handleUnmute = () => {
                    console.log('✅ Video track unmuted, starting playback');
                    playVideo();
                    videoTrack.removeEventListener('unmute', handleUnmute);
                };
                videoTrack.addEventListener('unmute', handleUnmute);
                
                // Also try playing after a delay as fallback
                setTimeout(() => {
                    if (videoTrack.muted) {
                        console.warn('Track still muted after 1s, trying to play anyway');
                    }
                    playVideo();
                }, 1000);
            } else {
                // Track is already unmuted, play immediately
                playVideo();
            }

            // Listen for track events
            const handleTrackEnded = () => {
                console.warn('Video track ended unexpectedly');
            };
            
            videoTracks.forEach(track => {
                track.addEventListener('ended', handleTrackEnded);
            });

            return () => {
                videoTracks.forEach(track => {
                    track.removeEventListener('ended', handleTrackEnded);
                });
            };
        }
    }, [videoElement, stream]);

    const receiverAudioStream = receiverAudioTarget?.stream;
    React.useEffect(() => {
        if (!receiverAudioStream) return;
        const bump = () => setReceiverAudioTracksEpoch((n) => n + 1);
        const onTrack = (t: MediaStreamTrack) => {
            if (t.kind === 'audio') {
                t.addEventListener('unmute', bump);
                t.addEventListener('mute', bump);
                t.addEventListener('ended', bump);
            }
        };
        const onAddTrack = (e: MediaStreamTrackEvent) => {
            bump();
            onTrack(e.track);
        };
        receiverAudioStream.addEventListener('addtrack', onAddTrack);
        receiverAudioStream.getAudioTracks().forEach(onTrack);
        return () => {
            receiverAudioStream.removeEventListener('addtrack', onAddTrack);
            receiverAudioStream.getAudioTracks().forEach((t) => {
                t.removeEventListener('unmute', bump);
                t.removeEventListener('mute', bump);
                t.removeEventListener('ended', bump);
            });
        };
    }, [receiverAudioStream]);

    const receiverHasAudioTrack =
        (receiverAudioStream?.getAudioTracks().filter((t) => t.readyState === 'live').length ?? 0) > 0;

    const onReceiverAudioTarget = React.useCallback(
        (target: { video: FullScreenHTMLVideoElement; stream: MediaStream } | null) => {
            setReceiverAudioTarget(target);
        },
        []
    );

    React.useEffect(() => {
        receiverAudioVideoRef.current = receiverAudioTarget?.video ?? null;
    }, [receiverAudioTarget]);

    React.useEffect(() => {
        audioPolicyHintShownRef.current = false;
        receiverInitialAudioHintShownRef.current = false;
    }, [state.id]);

    React.useEffect(() => {
        setReceiverPlaybackUnlocked(false);
    }, [state.id]);

    React.useEffect(() => {
        if (role !== 'receiver' || !receiverHasAudioTrack || receiverPlaybackUnlocked) return;
        if (receiverInitialAudioHintShownRef.current) return;
        receiverInitialAudioHintShownRef.current = true;
        enqueueSnackbar(
            'Audio is muted. Tap Unmute or adjust volume to hear it',
            { variant: 'info', autoHideDuration: 9000 }
        );
    }, [role, receiverHasAudioTrack, receiverPlaybackUnlocked, enqueueSnackbar]);

    const handleReceiverAutoplayAudioBlocked = React.useCallback(() => {
        setReceiverAudioPolicyBlocked(true);
        if (!audioPolicyHintShownRef.current) {
            audioPolicyHintShownRef.current = true;
            enqueueSnackbar(
                'Tap Unmute or move the volume slider to hear share audio.',
                { variant: 'info', autoHideDuration: 8000 }
            );
        }
    }, [enqueueSnackbar]);

    const onReceiverVolumeChange = React.useCallback((v: number) => {
        setReceiverPlaybackUnlocked(true);
        setReceiverMasterVolume(v);
        if (v > 0.001) {
            setReceiverMasterMuted(false);
            setReceiverAudioPolicyBlocked(false);
        }
        const vid = receiverAudioVideoRef.current;
        if (vid) {
            vid.volume = v;
            if (v > 0.001) {
                vid.muted = false;
                void vid.play();
            }
        }
    }, []);

    const onReceiverMutedToggle = React.useCallback(() => {
        setReceiverPlaybackUnlocked(true);
        setReceiverAudioPolicyBlocked(false);
        setReceiverMasterMuted((prev) => {
            const next = !prev;
            const vid = receiverAudioVideoRef.current;
            if (vid) {
                vid.muted = next;
                if (!next) {
                    void vid.play();
                }
            }
            return next;
        });
    }, []);

    const onReceiverAnnotationToggle = React.useCallback(() => {
        console.log('footer Annotate clicked', {
            eligibleCount: receiverAnnotationEligibleStreams.length,
            eligibleIds: receiverAnnotationEligibleStreams.map((s) => s.id),
        });
        setReceiverAnnotationEnabled((prev) => {
            if (prev) {
                console.log('toggle → OFF');
                setReceiverActiveAnnotationStreamId(null);
                return false;
            }
            if (receiverAnnotationEligibleStreams.length === 0) {
                console.log('toggle → stay OFF (no eligible streams)');
                return false;
            }
            const id = receiverAnnotationEligibleStreams[0].id;
            console.log('toggle → ON', {activeStreamId: id});
            setReceiverActiveAnnotationStreamId(id);
            return true;
        });
    }, [receiverAnnotationEligibleStreams]);

    const setHoverState = React.useMemo(
        () => ({
            onMouseLeave: () => setHoverControl(false),
            onMouseEnter: () => setHoverControl(true),
        }),
        [setHoverControl]
    );

    const controlVisible =
        (showControl || open || hoverControl) && !receiverStreamFullscreen;

    useHotkeys(
        'escape',
        () => {
            void handleExitFullscreen();
        },
        { enabled: receiverStreamFullscreen },
        [handleExitFullscreen, receiverStreamFullscreen]
    );

    useHotkeys('s', () => (state.hostStream ? stopShare() : share()), [state.hostStream, role]);
    useHotkeys(
        'f',
        () => {
            if (selectedStream) {
                handleFullscreen();
            }
        },
        [handleFullscreen, selectedStream]
    );
    useHotkeys(
        'h',
        () => {
            if (state.clientStreams !== undefined && state.clientStreams.length > 0) {
                const currentStreamIndex = state.clientStreams.findIndex(
                    ({ id }) => id === selectedStream
                );
                const nextIndex =
                    currentStreamIndex === state.clientStreams.length - 1
                        ? 0
                        : currentStreamIndex + 1;
                setSelectedStream(state.clientStreams[nextIndex].id);
            }
        },
        [state.clientStreams, selectedStream]
    );
    useHotkeys(
        'l',
        () => {
            if (state.clientStreams !== undefined && state.clientStreams.length > 0) {
                const currentStreamIndex = state.clientStreams.findIndex(
                    ({ id }) => id === selectedStream
                );
                const previousIndex =
                    currentStreamIndex === 0
                        ? state.clientStreams.length - 1
                        : currentStreamIndex - 1;
                setSelectedStream(state.clientStreams[previousIndex].id);
            }
        },
        [state.clientStreams, selectedStream]
    );
    useHotkeys(
        'm',
        () => {
            if (role === 'receiver' && state.clientStreams.length > 0) {
                setReceiverPlaybackUnlocked(true);
                setReceiverAudioPolicyBlocked(false);
                setReceiverMasterMuted((prev) => {
                    const next = !prev;
                    const vid = receiverAudioVideoRef.current;
                    if (vid) {
                        vid.muted = next;
                        if (!next) void vid.play();
                    }
                    return next;
                });
                return;
            }
            if (videoElement) {
                videoElement.muted = !videoElement.muted;
            }
        },
        [videoElement, role, state.clientStreams.length]
    );

    const receiverDisplayMuted =
        !receiverPlaybackUnlocked ||
        receiverMasterMuted ||
        receiverMasterVolume <= 0.001 ||
        receiverAudioPolicyBlocked;

    /**
     * Focused stream id for sidebar layout. Must never be `''` — that is falsy and used to gate
     * the sidebar branch; `''` incorrectly fell through to the democratic grid while UI showed "Sidebar".
     */
    const sidebarFocusId: string | null =
        state.clientStreams.length === 0
            ? null
            : mainStreamId && state.clientStreams.some((c) => c.id === mainStreamId)
              ? mainStreamId
              : state.clientStreams[state.clientStreams.length - 1]!.id;
    const statsPeerDisplayNames = React.useMemo(() => {
        const out: Record<string, string> = {};
        for (const cs of state.clientStreams) {
            const name = state.users.find((u) => u.id === cs.peer_id)?.name?.trim();
            out[cs.id] = name && name.length > 0 ? name : 'Unknown sender';
        }
        return out;
    }, [state.clientStreams, state.users]);
    const receiverNameForSenderStats = React.useMemo(() => {
        const receiver = state.users.find((u) => u.role === 'receiver');
        return receiver?.name?.trim() ?? '';
    }, [state.users]);
    const setStatsProbeVideoRef = React.useCallback((sessionId: string, el: HTMLVideoElement | null) => {
        if (!sessionId) return;
        if (el) statsVideoRefs.current.set(sessionId, el);
        else statsVideoRefs.current.delete(sessionId);
    }, []);
    const getReceiverProbeVideo = React.useCallback((peerId: string) => {
        return statsVideoRefs.current.get(peerId) ?? null;
    }, []);
    const getSenderProbeVideo = React.useCallback(() => videoElement, [videoElement]);

    const videoClasses = () => {
        const baseClasses = 'block mx-auto';
        switch (settings.displayMode) {
            case VideoDisplayMode.FitToWindow:
                return `${baseClasses} w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`;
            case VideoDisplayMode.OriginalSize:
                return baseClasses;
            case VideoDisplayMode.FitWidth:
                return `${baseClasses} w-full h-auto`;
            case VideoDisplayMode.FitHeight:
                return `${baseClasses} h-full w-auto`;
        }
    };

    return (
        <BackgroundWrapper hasScreens={state.clientStreams.length > 0}>
        <div className="absolute inset-0 flex min-h-0 flex-col overflow-hidden">
            <WebRTCStatsFloating
                role={role === 'receiver' ? 'receiver' : 'sender'}
                getPeers={getPeerConnections}
                active={role === 'receiver' ? state.clientStreams.length > 0 : !!state.hostStream}
                peerDisplayNames={role === 'receiver' ? statsPeerDisplayNames : undefined}
                remoteFallbackName={role !== 'receiver' ? receiverNameForSenderStats : undefined}
                panelDisplayName={state.displayName}
                getReceiverProbeVideo={role === 'receiver' ? getReceiverProbeVideo : undefined}
                getSenderProbeVideo={role !== 'receiver' ? getSenderProbeVideo : undefined}
            />
            {/* Footer Bar — hidden in receiver single-stream fullscreen (floating controls only). */}
            {!receiverStreamFullscreen && (
                <FooterBar
                    roomId={state.id}
                    roomDisplayName={state.displayName}
                    onRoomDisplayNameSave={role === 'receiver' ? setRoomDisplayName : undefined}
                    participantCount={state.users.length}
                    onMembersClick={() => setShowMembers(true)}
                    viewMode={viewMode}
                    onViewModeChange={handleViewModeChange}
                    receiverAudioUi={role === 'receiver' && state.clientStreams.length > 0}
                    receiverVolume={receiverMasterVolume}
                    receiverDisplayMuted={receiverDisplayMuted}
                    receiverAudioNeedsInteraction={
                        role === 'receiver' && receiverHasAudioTrack && !receiverPlaybackUnlocked
                    }
                    onReceiverVolumeChange={onReceiverVolumeChange}
                    onReceiverMutedToggle={onReceiverMutedToggle}
                    receiverHasAudioTrack={role === 'receiver' ? receiverHasAudioTrack : false}
                    receiverAnnotationUi={role === 'receiver' && state.clientStreams.length > 0}
                    receiverAnnotationEnabled={receiverAnnotationEnabled}
                    onReceiverAnnotationToggle={onReceiverAnnotationToggle}
                />
            )}

            {/* Main Video / Grid — flex-1 so receiver sidebar/grid get a real height (footer is fixed). */}
            <div
                className={`relative min-h-0 w-full flex-1 overflow-auto ${
                    role === 'receiver' && !receiverStreamFullscreen
                        ? 'pb-[max(68px,calc(56px+env(safe-area-inset-bottom)))]'
                        : ''
                }`}
            >
            {role === 'receiver' && fullscreenStreamId ? (
                /* Immersive: `requestFullscreen` on this root; exit control on video overlay; bottom = audio only. */
                <div
                    ref={receiverImmersiveRootRef}
                    className="fixed inset-0 z-[30] flex min-h-0 w-full flex-col bg-black [&:fullscreen]:h-full"
                >
                    <div className="relative min-h-0 w-full flex-1">
                        {(() => {
                            const fullscreenClient = state.clientStreams.find(
                                (c) => c.id === fullscreenStreamId
                            );
                            if (!fullscreenClient) return null;
                            const userName =
                                state.users.find(({ id }) => fullscreenClient.peer_id === id)?.name ??
                                'unknown';
                            return (
                                <GridVideoItem
                                    stream={fullscreenClient.stream}
                                    userName={userName}
                                    isFullscreen={true}
                                    onMinimize={() => void handleExitFullscreen()}
                                    playAudio={role === 'receiver'}
                                    onReceiverAudioTarget={
                                        role === 'receiver' ? onReceiverAudioTarget : undefined
                                    }
                                    masterVolume={receiverMasterVolume}
                                    masterMuted={receiverMasterMuted}
                                    policyMute={receiverAudioPolicyBlocked}
                                    playbackAudioUnlocked={receiverPlaybackUnlocked}
                                    onAutoplayAudioBlocked={handleReceiverAutoplayAudioBlocked}
                                    sessionLayoutKey={fullscreenClient.id}
                                    annotationChannel={fullscreenClient.annotationChannel}
                                    videoLayoutRevision={settings.displayMode}
                                    allowDraw={role === 'receiver'}
                                    annotationUserId={meUserId}
                                    annotationModeEnabled={receiverAnnotationEnabled}
                                    annotationActive={
                                        receiverAnnotationEnabled &&
                                        receiverActiveAnnotationStreamId === fullscreenClient.id
                                    }
                                    onActivateAnnotation={() => {
                                        if (!receiverAnnotationEnabled) return;
                                        setReceiverActiveAnnotationStreamId(fullscreenClient.id);
                                    }}
                                    onDeactivateAnnotation={() => {
                                        setReceiverAnnotationEnabled(false);
                                        setReceiverActiveAnnotationStreamId(null);
                                    }}
                                    onVideoRef={(el) => setStatsProbeVideoRef(fullscreenClient.id, el)}
                                />
                            );
                        })()}
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[45] flex justify-center p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                            <LiquidGlassCard className="pointer-events-auto flex flex-col items-center gap-1 border-white/20 bg-black/75 px-3 py-2.5 shadow-lg backdrop-blur-xl">
                                <StreamAudioControl
                                    volume={receiverMasterVolume}
                                    displayMuted={receiverDisplayMuted}
                                    onVolumeChange={onReceiverVolumeChange}
                                    onMutedToggle={onReceiverMutedToggle}
                                    hasAudioTrack={receiverHasAudioTrack}
                                    needsUserGesture={
                                        receiverHasAudioTrack && !receiverPlaybackUnlocked
                                    }
                                />
                            </LiquidGlassCard>
                        </div>
                    </div>
                </div>
            ) : gridView && role === 'receiver' ? (
                /* Grid View for Receivers — stretch so sidebar / grid get real height (not centered zero flex). */
                <div className="relative flex h-full min-h-0 w-full flex-col p-0">
                    {state.clientStreams.length === 0 ? (
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/60 text-4xl">
                            no stream available
                        </div>
                    ) : viewMode === 'sidebar' && sidebarFocusId != null ? (
                        /* Sidebar (focused) — main stage + vertical thumbnails on md+ (server-master parity). */
                        <div className="box-border flex h-full min-h-0 w-full flex-1 flex-col bg-black px-2 pb-2 pt-2 md:px-3 md:pb-3 md:pt-3">
                            <FocusedSidebarLayout
                                streams={state.clientStreams}
                                focusedId={sidebarFocusId}
                                onFocusChange={handleSidebarFocusChange}
                                onMainDoubleClick={(id) => handleEnterFullscreen(id)}
                                onThumbDoubleClick={(id) => handleEnterFullscreen(id)}
                                className="min-h-0 flex-1"
                                renderTile={({ stream: client, isMainStage }) => {
                                    const userName =
                                        state.users.find(({ id }) => client.peer_id === id)?.name ??
                                        'unknown';
                                    return (
                                        <GridVideoItem
                                            stream={client.stream}
                                            userName={userName}
                                            onFullscreen={() => handleEnterFullscreen(client.id)}
                                            playAudio={role === 'receiver' && isMainStage}
                                            onReceiverAudioTarget={
                                                role === 'receiver' && isMainStage
                                                    ? onReceiverAudioTarget
                                                    : undefined
                                            }
                                            masterVolume={receiverMasterVolume}
                                            masterMuted={receiverMasterMuted}
                                            policyMute={receiverAudioPolicyBlocked}
                                            playbackAudioUnlocked={receiverPlaybackUnlocked}
                                            onAutoplayAudioBlocked={
                                                isMainStage
                                                    ? handleReceiverAutoplayAudioBlocked
                                                    : undefined
                                            }
                                            sessionLayoutKey={client.id}
                                            annotationChannel={client.annotationChannel}
                                            videoLayoutRevision={settings.displayMode}
                                            allowDraw={role === 'receiver'}
                                            annotationUserId={meUserId}
                                            annotationModeEnabled={receiverAnnotationEnabled}
                                            annotationActive={
                                                receiverAnnotationEnabled &&
                                                receiverActiveAnnotationStreamId === client.id
                                            }
                                            onActivateAnnotation={() => {
                                                if (!receiverAnnotationEnabled) return;
                                                setReceiverActiveAnnotationStreamId(client.id);
                                            }}
                                            onDeactivateAnnotation={() => {
                                                setReceiverAnnotationEnabled(false);
                                                setReceiverActiveAnnotationStreamId(null);
                                            }}
                                            onVideoRef={(el) => setStatsProbeVideoRef(client.id, el)}
                                            pauseInfo={state.sharePauseByPeer?.[client.peer_id]}
                                        />
                                    );
                                }}
                            />
                        </div>
                    ) : (
                        /* Democratic/Custom Mode */
                        <>
                            {/* Grid Container */}
                            <div className={`grid ${getGridLayoutClass()} gap-1 w-full h-full`}>
                                {getVisibleStreams().map((client, tileIndex) => {
                                        const userName = state.users.find(({ id }) => client.peer_id === id)?.name ?? 'unknown';
                                        const isAudioTile = role === 'receiver' && tileIndex === 0;
                                        return (
                                            <GridVideoItem
                                                key={client.id}
                                                stream={client.stream}
                                                userName={userName}
                                                onFullscreen={() => handleEnterFullscreen(client.id)}
                                                playAudio={isAudioTile}
                                                onReceiverAudioTarget={
                                                    isAudioTile ? onReceiverAudioTarget : undefined
                                                }
                                                masterVolume={receiverMasterVolume}
                                                masterMuted={receiverMasterMuted}
                                                policyMute={receiverAudioPolicyBlocked}
                                                playbackAudioUnlocked={receiverPlaybackUnlocked}
                                                onAutoplayAudioBlocked={
                                                    isAudioTile ? handleReceiverAutoplayAudioBlocked : undefined
                                                }
                                                sessionLayoutKey={client.id}
                                                annotationChannel={client.annotationChannel}
                                                videoLayoutRevision={settings.displayMode}
                                                allowDraw={role === 'receiver'}
                                                annotationUserId={meUserId}
                                                annotationModeEnabled={receiverAnnotationEnabled}
                                                annotationActive={
                                                    receiverAnnotationEnabled &&
                                                    receiverActiveAnnotationStreamId === client.id
                                                }
                                                onActivateAnnotation={() => {
                                                    if (!receiverAnnotationEnabled) return;
                                                    setReceiverActiveAnnotationStreamId(client.id);
                                                }}
                                                onDeactivateAnnotation={() => {
                                                    setReceiverAnnotationEnabled(false);
                                                    setReceiverActiveAnnotationStreamId(null);
                                                }}
                                                onVideoRef={(el) => setStatsProbeVideoRef(client.id, el)}
                                                pauseInfo={state.sharePauseByPeer?.[client.peer_id]}
                                            />
                                        );
                                    })}
                            </div>

                            {/* Pagination Controls - hide in custom mode */}
                            {viewMode === 'democratic' && state.clientStreams.length > STREAMS_PER_PAGE && (
                                <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex items-center gap-3">
                                    <Tooltip content="Previous Page" side="top">
                                        <LiquidButton
                                            onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                                            disabled={currentPage === 0}
                                            variant="ghost"
                                            size="icon"
                                            className="bg-black/40 hover:bg-black/60 text-white border-white/10 disabled:opacity-30 w-12 h-12"
                                        >
                                            <ChevronLeft className="w-6 h-6" />
                                        </LiquidButton>
                                    </Tooltip>

                                    <LiquidGlassCard className="border-white/20 bg-black/60 backdrop-blur-xl px-4 py-2">
                                        <span className="text-white font-medium">
                                            {currentPage + 1} / {Math.ceil(state.clientStreams.length / STREAMS_PER_PAGE)}
                                        </span>
                                    </LiquidGlassCard>

                                    <Tooltip content="Next Page" side="top">
                                        <LiquidButton
                                            onClick={() =>
                                                setCurrentPage((p) =>
                                                    Math.min(Math.ceil(state.clientStreams.length / STREAMS_PER_PAGE) - 1, p + 1)
                                                )
                                            }
                                            disabled={currentPage >= Math.ceil(state.clientStreams.length / STREAMS_PER_PAGE) - 1}
                                            variant="ghost"
                                            size="icon"
                                            className="bg-black/40 hover:bg-black/60 text-white border-white/10 disabled:opacity-30 w-12 h-12"
                                        >
                                            <ChevronRight className="w-6 h-6" />
                                        </LiquidButton>
                                    </Tooltip>
                                </div>
                            )}
                        </>
                    )}
                </div>
            ) : (
                /* Single Video View */
                stream ? (
                    <>
                        <video
                            ref={setVideoElement}
                            className={videoClasses()}
                            onDoubleClick={handleFullscreen}
                            autoPlay
                            playsInline
                            muted={false}
                            onLoadedMetadata={() => console.log('Video metadata loaded')}
                            onLoadedData={() => console.log('Video data loaded')}
                            onPlaying={() => console.log('Video playing')}
                            onWaiting={() => console.log('Video waiting')}
                        />
                        {/* Show loading overlay if video element exists but hasn't started playing */}
                        {videoElement && videoElement.readyState < 2 && (
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/60 text-center">
                                <div className="animate-pulse">
                                    <div className="text-4xl mb-4">⏳</div>
                                    <div className="text-xl">Loading stream...</div>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/60 text-4xl">
                        no stream available
                    </div>
                )
            )}
            </div>

            {/* Bottom Controls */}
            <AnimatePresence>
                {controlVisible && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.3 }}
                        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30"
                        {...setHoverState}
                    >
                        <LiquidGlassCard className="border-white/20 bg-black/60 backdrop-blur-xl px-6 py-4 shadow-lg">
                            <div className="flex items-center gap-3">
                                {/* Audio Control (sender / non-receiver; receiver uses footer + grid unmute) */}
                                {role !== 'receiver' &&
                                    (stream?.getAudioTracks().length ?? 0) > 0 &&
                                    videoElement && <AudioControl video={videoElement} />}

                                {/* Share/Stop Share Button */}
                                {role !== 'receiver' && (state.hostStream ? (
                                    <>
                                        <Tooltip
                                            content={
                                                sharePauseLocal.isPaused ? 'Resume Sharing' : 'Pause Sharing'
                                            }
                                            side="top"
                                        >
                                            <LiquidButton
                                                onClick={togglePauseShare}
                                                variant="ghost"
                                                size="icon"
                                                className="bg-amber-500/20 hover:bg-amber-500/30 text-white border-0 w-14 h-14"
                                            >
                                                {sharePauseLocal.isPaused ? (
                                                    <Play className="w-8 h-8" />
                                                ) : (
                                                    <Pause className="w-8 h-8" />
                                                )}
                                            </LiquidButton>
                                        </Tooltip>
                                        <Tooltip content="Stop Sharing" side="top">
                                            <LiquidButton
                                                onClick={stopShare}
                                                variant="ghost"
                                                size="icon"
                                                className="bg-red-500/20 hover:bg-red-500/30 text-white border-0 w-14 h-14"
                                            >
                                                <MonitorOff className="w-8 h-8" />
                                            </LiquidButton>
                                        </Tooltip>
                                    </>
                                    ) : (
                                    <Tooltip content="Start Sharing" side="top">
                                        <LiquidButton
                                            onClick={share}
                                            variant="ghost"
                                            size="icon"
                                            className="bg-black/40 hover:bg-black/60 text-white border-white/10 w-14 h-14"
                                        >
                                            <Monitor className="w-8 h-8" />
                                        </LiquidButton>
                                    </Tooltip>
                                    ))}

                                {/* Fullscreen Button - Hidden in grid view */}
                                {!(role === 'receiver' && gridView) && (
                                    <Tooltip content="Fullscreen" side="top">
                                        <LiquidButton
                                            onClick={handleFullscreen}
                                            disabled={!selectedStream}
                                            variant="ghost"
                                            size="icon"
                                            className="bg-black/40 hover:bg-black/60 text-white border-white/10 disabled:opacity-30 w-14 h-14"
                                        >
                                            <Maximize2 className="w-8 h-8" />
                                        </LiquidButton>
                                    </Tooltip>
                                )}

                                {/* Settings Button - Commented out for receivers */}
                                {/* <Tooltip content="Settings" side="top">
                                    <LiquidButton
                                        onClick={() => setOpen(true)}
                                        variant="ghost"
                                        size="icon"
                                        className="bg-black/40 hover:bg-black/60 text-white border-white/10 w-14 h-14"
                                    >
                                        <Settings className="w-8 h-8" />
                                    </LiquidButton>
                                </Tooltip> */}
                            </div>
                        </LiquidGlassCard>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Video Thumbnails - Hidden for receivers in grid view */}
            {!(role === 'receiver' && gridView) && (
            <div className="fixed bottom-0 right-0 z-20 flex gap-2 p-2">
                {state.clientStreams
                    .filter(({ id }) => id !== selectedStream)
                    .map((client) => {
                        const userName =
                            state.users.find(({ id }) => client.peer_id === id)?.name ?? 'unknown';
                        return (
                            <motion.div
                                key={client.id}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                onClick={() => setSelectedStream(client.id)}
                                className="cursor-pointer"
                            >
                                <LiquidGlassCard className="border-white/10 bg-white/5 backdrop-blur-xl p-1 max-w-[300px] max-h-[200px] relative overflow-hidden">
                                    <ReceiverAnnotationOverlay
                                        channel={client.annotationChannel}
                                        allowDraw={role === 'receiver'}
                                        userId={meUserId}
                                        layoutKey={client.id}
                                        videoLayoutRevision={settings.displayMode}
                                        canvasSyncEpoch={0}
                                    >
                                        <Video
                                            key={client.id}
                                            src={client.stream}
                                            className="min-w-full min-h-full w-auto max-w-[300px] max-h-[200px]"
                                        />
                                    </ReceiverAnnotationOverlay>
                                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 backdrop-blur-sm px-3 py-1">
                                        <span className="text-white text-sm">{userName}</span>
                                    </div>
                                </LiquidGlassCard>
                            </motion.div>
                        );
                    })}
                {state.hostStream && selectedStream !== HostStream && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        onClick={() => setSelectedStream(HostStream)}
                        className="cursor-pointer"
                    >
                        <LiquidGlassCard className="border-white/10 bg-white/5 backdrop-blur-xl p-1 max-w-[300px] max-h-[200px] relative overflow-hidden">
                            <Video
                                src={state.hostStream}
                                className="min-w-full min-h-full w-auto max-w-[300px] max-h-[200px]"
                            />
                            <div className="absolute bottom-0 left-0 right-0 bg-black/50 backdrop-blur-sm px-3 py-1">
                                <span className="text-white text-sm">You</span>
                            </div>
                        </LiquidGlassCard>
                    </motion.div>
                )}
            </div>
            )}

            {/* Members Panel */}
            <MembersPanel
                isOpen={showMembers}
                onClose={() => setShowMembers(false)}
                users={state.users}
                roomDisplayName={state.displayName}
                onRoomDisplayNameSave={role === 'receiver' ? setRoomDisplayName : undefined}
                selectedLayout={forcedLayout}
                onLayoutChange={handleLayoutChange}
                onFullscreen={handleMemberFullscreen}
                activeFullscreenPeerId={membersPanelFullscreenPeerId}
                onToggleHide={handleToggleHideUser}
                hiddenUserIds={hiddenUserIds}
            />

            {/* Settings Dialog */}
            <SettingDialog
                open={open}
                setOpen={setOpen}
                updateName={setName}
                saveSettings={setSettings}
                role={role}
            />
        </div>
        </BackgroundWrapper>
    );
};

const useShowOnMouseMovement = (doShow: (s: boolean) => void) => {
    const timeoutHandle = React.useRef(0);

    React.useEffect(() => {
        const update = () => {
            if (timeoutHandle.current === 0) {
                doShow(true);
            }

            clearTimeout(timeoutHandle.current);
            timeoutHandle.current = window.setTimeout(() => {
                timeoutHandle.current = 0;
                doShow(false);
            }, 1000);
        };
        window.addEventListener('mousemove', update);
        return () => window.removeEventListener('mousemove', update);
    }, [doShow]);

    React.useEffect(
        () =>
            void (timeoutHandle.current = window.setTimeout(() => {
                timeoutHandle.current = 0;
                doShow(false);
            }, 1000)),
        []
    );
};

const AudioControl = ({ video }: { video: FullScreenHTMLVideoElement }) => {
    const [, setMuted] = React.useState<boolean>();

    React.useEffect(() => {
        const handler = () => setMuted(video.muted);
        video.addEventListener('volumechange', handler);
        setMuted(video.muted);
        return () => video.removeEventListener('volumechange', handler);
    });

    return (
        <div className="flex items-center gap-2">
            <LiquidButton
                onClick={() => (video.muted = !video.muted)}
                variant="ghost"
                size="icon"
                className="bg-white/5 hover:bg-white/10 text-white border-0 w-12 h-12"
            >
                {video.muted ? <VolumeX className="w-7 h-7" /> : <Volume2 className="w-7 h-7" />}
            </LiquidButton>
            <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                defaultValue={video.volume}
                onChange={(e) => {
                    video.muted = false;
                    video.volume = parseFloat(e.target.value);
                }}
                className="w-28 h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer accent-white"
            />
        </div>
    );
};

interface GridVideoItemProps {
    stream: MediaStream;
    userName: string;
    isFullscreen?: boolean;
    /** Immersive receiver mode: exit browser / tile fullscreen (top-right on hover). */
    onMinimize?: () => void;
    onFullscreen?: () => void;
    /** Receiver only: unmute this tile so tab/system audio is audible (other tiles stay muted). */
    playAudio?: boolean;
    onReceiverAudioTarget?: (
        target: { video: FullScreenHTMLVideoElement; stream: MediaStream } | null
    ) => void;
    /** Master volume from footer — applied to every tile’s `<video>`. */
    masterVolume?: number;
    masterMuted?: boolean;
    /** Receiver: keep video muted until user gesture (autoplay policy). */
    policyMute?: boolean;
    /** Receiver: false until user uses volume or Unmute — avoids programmatic unmute pausing video. */
    playbackAudioUnlocked?: boolean;
    /** Fires when the browser refuses unmuted playback for the audio tile. */
    onAutoplayAudioBlocked?: () => void;
    /** WebRTC session id — annotation engine + overlay alignment. */
    sessionLayoutKey?: string;
    annotationChannel?: RTCDataChannel;
    videoLayoutRevision?: VideoDisplayMode;
    allowDraw?: boolean;
    annotationUserId?: string;
    annotationModeEnabled?: boolean;
    annotationActive?: boolean;
    onActivateAnnotation?: () => void;
    onDeactivateAnnotation?: () => void;
    onVideoRef?: (el: HTMLVideoElement | null) => void;
    pauseInfo?: {paused: boolean; mode: 'soft' | 'hard'};
}

const GridVideoItem: React.FC<GridVideoItemProps> = ({
    stream,
    userName,
    isFullscreen = false,
    onMinimize,
    onFullscreen,
    playAudio = false,
    onReceiverAudioTarget,
    masterVolume = 1,
    masterMuted = false,
    policyMute = false,
    playbackAudioUnlocked = false,
    onAutoplayAudioBlocked,
    sessionLayoutKey,
    annotationChannel,
    videoLayoutRevision,
    allowDraw = false,
    annotationUserId = '',
    annotationModeEnabled = false,
    annotationActive = false,
    onActivateAnnotation,
    onDeactivateAnnotation,
    onVideoRef,
    pauseInfo,
}) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [videoElement, setVideoElement] = React.useState<HTMLVideoElement | null>(null);
    const [videoAspectRatio, setVideoAspectRatio] = React.useState<number | null>(null);
    /** After first successful play(), safe to sync mute from props without re-attaching stream. */
    const [gridPlaybackReady, setGridPlaybackReady] = React.useState(false);
    const playAudioRef = React.useRef(playAudio);
    const masterMutedRef = React.useRef(masterMuted);
    const policyMuteRef = React.useRef(policyMute);
    const masterVolumeRef = React.useRef(masterVolume);
    const playbackUnlockedRef = React.useRef(playbackAudioUnlocked);
    React.useEffect(() => {
        if (!sessionLayoutKey || !annotationModeEnabled || !annotationActive) return;
        console.log('GridVideoItem (active tile for toolbar)', {
            sessionLayoutKey,
            hasAnnotationUserId: Boolean(annotationUserId),
        });
    }, [sessionLayoutKey, annotationModeEnabled, annotationActive, annotationUserId]);

    playAudioRef.current = playAudio;
    masterMutedRef.current = masterMuted;
    policyMuteRef.current = policyMute;
    masterVolumeRef.current = masterVolume;
    playbackUnlockedRef.current = playbackAudioUnlocked;

    const applyDesiredMuted = React.useCallback((el: HTMLVideoElement) => {
        const keepMutedForNoGesture =
            playAudioRef.current && !playbackUnlockedRef.current;
        el.muted =
            !playAudioRef.current ||
            masterMutedRef.current ||
            policyMuteRef.current ||
            keepMutedForNoGesture;
    }, []);

    // Stream attach + play only when stream or element changes (not when mute/volume props alone change — avoids black flashes).
    React.useEffect(() => {
        if (!videoElement) return;
        let cancelled = false;
        setGridPlaybackReady(false);

        const startPlayback = () => {
            videoElement.srcObject = stream;
            // Autoplay policy: must start muted, then apply desired mute after play() resolves.
            videoElement.muted = true;
            return videoElement
                .play()
                .then(() => {
                    if (cancelled) return;
                    applyDesiredMuted(videoElement);
                    setGridPlaybackReady(true);
                    queueMicrotask(() => {
                        if (cancelled || !videoElement) return;
                        const wantSound =
                            playAudioRef.current &&
                            !masterMutedRef.current &&
                            masterVolumeRef.current > 0.001 &&
                            !policyMuteRef.current &&
                            playbackUnlockedRef.current;
                        if (
                            wantSound &&
                            (videoElement.muted || videoElement.paused) &&
                            onAutoplayAudioBlocked
                        ) {
                            onAutoplayAudioBlocked();
                        }
                    });
                })
                .catch((e: DOMException) => {
                    if (e?.name === 'AbortError') return;
                    console.log('Could not play grid video', e);
                    if (e?.name === 'NotAllowedError' && videoElement && !cancelled) {
                        videoElement.muted = true;
                        return videoElement.play().then(() => {
                            if (cancelled) return;
                            applyDesiredMuted(videoElement);
                            setGridPlaybackReady(true);
                            queueMicrotask(() => {
                                if (cancelled || !videoElement) return;
                                const wantSound =
                                    playAudioRef.current &&
                                    !masterMutedRef.current &&
                                    masterVolumeRef.current > 0.001 &&
                                    !policyMuteRef.current &&
                                    playbackUnlockedRef.current;
                                if (
                                    wantSound &&
                                    (videoElement.muted || videoElement.paused) &&
                                    onAutoplayAudioBlocked
                                ) {
                                    onAutoplayAudioBlocked();
                                }
                            });
                        });
                    }
                });
        };

        void startPlayback();
        return () => {
            cancelled = true;
        };
    }, [videoElement, stream, applyDesiredMuted, onAutoplayAudioBlocked]);

    React.useEffect(() => {
        if (!videoElement) return;
        videoElement.volume = masterVolume;
    }, [videoElement, masterVolume, stream]);

    React.useEffect(() => {
        if (!videoElement) return;
        const updateRatio = () => {
            const w = videoElement.videoWidth;
            const h = videoElement.videoHeight;
            if (w > 0 && h > 0) {
                setVideoAspectRatio(w / h);
            }
        };
        updateRatio();
        videoElement.addEventListener('loadedmetadata', updateRatio);
        videoElement.addEventListener('resize', updateRatio);
        return () => {
            videoElement.removeEventListener('loadedmetadata', updateRatio);
            videoElement.removeEventListener('resize', updateRatio);
        };
    }, [videoElement, stream]);

    // Sync mute when user toggles footer / layout (no stream re-attach).
    React.useEffect(() => {
        if (!videoElement || !gridPlaybackReady) return;
        const keepMutedForNoGesture = playAudio && !playbackAudioUnlocked;
        videoElement.muted = !playAudio || masterMuted || policyMute || keepMutedForNoGesture;
    }, [videoElement, playAudio, masterMuted, policyMute, playbackAudioUnlocked, gridPlaybackReady]);

    React.useEffect(() => {
        if (!playAudio || !videoElement || !onReceiverAudioTarget) return;
        onReceiverAudioTarget({ video: videoElement as FullScreenHTMLVideoElement, stream });
        return () => {
            onReceiverAudioTarget(null);
        };
    }, [playAudio, videoElement, stream, onReceiverAudioTarget]);

    const videoEl =
        sessionLayoutKey != null && sessionLayoutKey !== '' ? (
            <ReceiverAnnotationOverlay
                channel={annotationChannel}
                allowDraw={allowDraw}
                userId={annotationUserId}
                layoutKey={sessionLayoutKey}
                videoLayoutRevision={videoLayoutRevision}
                canvasSyncEpoch={0}
                    toolsVisible={annotationModeEnabled && annotationActive}
                    onToolsVisibleChange={(visible: boolean) => {
                        if (visible) onActivateAnnotation?.();
                        else onDeactivateAnnotation?.();
                    }}
                    showAnnotateButton={false}
                    toolbarPlacement="globalFooterCenter"
            >
                <GridVideoChild
                    autoPlay
                    playsInline
                    className="block h-full w-full object-contain object-center bg-black"
                    onBridge={setVideoElement}
                    onVideoRef={onVideoRef}
                />
            </ReceiverAnnotationOverlay>
        ) : (
            <video
                ref={(el) => {
                    setVideoElement(el);
                    onVideoRef?.(el);
                }}
                autoPlay
                playsInline
                className="block h-full w-full object-contain object-center bg-black"
            />
        );

    return (
        <div
            className="relative overflow-hidden group h-full w-full"
            onClick={() => {
                if (annotationModeEnabled) onActivateAnnotation?.();
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="grid h-full w-full place-items-center bg-black">
                <div
                    className="relative max-h-full max-w-full overflow-hidden"
                    style={videoAspectRatio ? {aspectRatio: videoAspectRatio, width: '100%', height: 'auto'} : {width: '100%', height: '100%'}}
                >
                    {videoEl}

                    {pauseInfo?.paused && (
                        <div
                            className={`pointer-events-none absolute inset-0 z-20 flex items-center justify-center px-3 text-center ${
                                pauseInfo.mode === 'hard' ? 'bg-slate-950/95' : 'bg-black/60'
                            }`}
                        >
                            <span className="text-sm font-semibold text-white">
                                {pauseInfo.mode === 'hard' ? 'No active screen stream' : 'Screen sharing paused'}
                            </span>
                        </div>
                    )}

                    {isFullscreen && onMinimize ? (
                        <AnimatePresence>
                            {isHovered && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="absolute top-2 right-2 z-[60]"
                                >
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onMinimize();
                                        }}
                                        className="rounded-lg bg-black/50 p-2.5 backdrop-blur-sm transition-colors hover:bg-black/70"
                                        title="Exit fullscreen (Esc)"
                                        aria-label="Exit fullscreen"
                                    >
                                        <Minimize2 className="h-5 w-5 text-white" />
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    ) : (
                        <>
                            <AnimatePresence>
                                {isHovered && onFullscreen && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="absolute top-2 left-2 z-[60]"
                                    >
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onFullscreen();
                                            }}
                                            className="rounded-lg bg-black/40 p-2 backdrop-blur-sm transition-colors hover:bg-black/60"
                                            title="Fullscreen"
                                        >
                                            <Maximize2 className="h-4 w-4 text-white" />
                                        </button>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <div className="absolute bottom-0 left-0 w-fit max-w-[calc(100%-0.5rem)] rounded-tr-lg bg-gradient-to-t from-black/85 via-black/55 to-transparent px-3 py-2 backdrop-blur-sm">
                                <span className="text-sm font-medium text-white">{userName}</span>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
